import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { setSupabaseAuth } from "./supabase";
import { supabase } from "./supabase";

// ── Analytics (UNCHANGED) ────────────────────────────────────
import { trackUserLogin } from "./analyticsService";

// ── PWA Service Worker ───────────────────────────────────────
import { registerServiceWorker } from "./registerSW";

// ── NEW: Route Guards ─────────────────────────────────────────
import MentorRoute from "./guards/MentorRoute";
import UserRoute   from "./guards/UserRoute";
import AdminRoute  from "./guards/AdminRoute";

// ── Components & Pages (ALL UNCHANGED) ───────────────────────
import Login            from "./components/Login";
import WelcomePrompt    from "./components/WelcomePrompt";
import Dashboard        from "./pages/Dashboard";
import Resources        from "./pages/Resources";
import Community        from "./pages/Community";
import AboutUs          from "./components/AboutUs";
import ChatSpace        from "./components/ChatSpace";
import AppointmentForm  from "./components/AppointmentForm";
import MyProfile        from "./components/MyProfile";
import ProfileSetupPage from "./ProfileSetup/ProfileSetupPage.jsx";
import Chattr           from "./pages/Chattr";
import CommunityFeed    from "./pages/CommunityFeed";
import Roadmap          from "./pages/Roadmap";
import ClueCuePage      from "./pages/ClueCuePage";
import { FeedProvider } from "./context/FeedContext";
import Chatbot          from "./components/Chatbot";
import Insight          from "./pages/Insight";
import DiaryVault       from "./pages/DiaryVault";
import Onboarding       from "./pages/Onboarding";
import Pricing          from "./pages/Pricing";

// ── Mentor System Pages (UNCHANGED) ──────────────────────────
import MentorsPage        from "./components/MentorsPage";
import MentorVerification from "./components/MentorVerification";
import MentorProfileSetup from "./components/MentorProfileSetup";
import MentorDashboard    from "./pages/MentorDashboard";
import AdminPanel         from "./pages/AdminPanel";

// ── NEW: LinkedIn OAuth callback ──────────────────────────────
import LinkedInCallback from "./components/LinkedInCallback";

// ── NEW: Mobile PWA Shell ─────────────────────────────────────
import MobileShell from "./mobile/MobileShell";
import JournalPage from "./mobile/pages/JournalPage";
import useIsMobile from "./hooks/useIsMobile";

// ── NEW: Imported Mini Games ───────────────────────────────────
import { 
  TicTacToe, 
  MemoryMatch, 
  NumberGuess, 
  WordScramble, 
  SnakeGame, 
  ReactionTest, 
  MathSprint, 
  TwoPlayerDuel 
} from "./mobile/pages/MiniGames";

// ── NEW: Detect user role from Supabase mentors table ─────────
async function detectRole(firebaseUser) {
  if (!firebaseUser) return null;

  // Check if admin
  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || "";
  if (firebaseUser.email === adminEmail) return "admin";

  // Check if approved mentor
  const { data: mentor } = await supabase
    .from("mentors")
    .select("id")
    .eq("email", firebaseUser.email)
    .maybeSingle();

  if (mentor) return "mentor";

  return "user";
}

function App() {
  const [user,       setUser]       = useState(null);
  const [role,       setRole]       = useState(null); // NEW: "user"|"mentor"|"admin"|null
  const [loading,    setLoading]    = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  // ── Mobile detection for responsive routing ─────────────────
  const isMobile = useIsMobile(860);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Sync with Supabase session (UNCHANGED)
      await setSupabaseAuth(currentUser);
      setUser(currentUser);

      if (currentUser) {
        // Run role detection + profile check in parallel (faster than sequential)
        const [detectedRole, docSnap] = await Promise.all([
          detectRole(currentUser),
          getDoc(doc(db, "users", currentUser.uid)),
        ]);

        setRole(detectedRole);
        setHasProfile(docSnap.data()?.profileCompleted === true);

        // Analytics (UNCHANGED)
        trackUserLogin(currentUser);
      } else {
        setRole(null);
        setHasProfile(false);
      }

      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading)
    return (
      <div className="loading-screen">
        <span className="hug-icon">💖🌿</span>
        <p>Preparing your healing space...</p>
      </div>
    );

  return (
    <Router>
      <Routes>
        {/* ── Public Routes (UNCHANGED) ──────────────────── */}
        <Route path="/" element={
  user ? <Navigate to={hasProfile ? "/dashboard" : "/welcome"} replace /> : <Login />
} />
        <Route path="/about"       element={<AboutUs />} />
        <Route path="/chatspace"   element={<ChatSpace />} />
        <Route path="/appointment" element={<AppointmentForm />} />

        {/* ── NEW: LinkedIn OAuth callback ───────────────── */}
        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />

        {/* ── Auth & Profile Flow (UNCHANGED logic) ─────── */}
        <Route path="/welcome" element={
          user ? (hasProfile ? <Navigate to="/dashboard" /> : <WelcomePrompt />) : <Navigate to="/" />
        }/>
        <Route path="/setup-profile" element={
          user ? (hasProfile ? <Navigate to="/dashboard" /> : <ProfileSetupPage />) : <Navigate to="/" />
        }/>
        <Route path="/onboarding" element={
          user ? <Onboarding /> : <Navigate to="/" />
        }/>

        {/* ── User Routes — mentors get redirected to /mentordashboard ── */}
        <Route path="/dashboard" element={
          <UserRoute user={user} role={role}>
            {isMobile ? <MobileShell user={user} role={role} /> : <Dashboard />}
          </UserRoute>
        } />
        <Route path="/resources"   element={<UserRoute user={user} role={role}><Resources /></UserRoute>} />
        <Route path="/chatbot"     element={<UserRoute user={user} role={role}><Chatbot /></UserRoute>} />
        <Route path="/community"   element={<UserRoute user={user} role={role}><Community /></UserRoute>} />
        <Route path="/insight"     element={<UserRoute user={user} role={role}><Insight /></UserRoute>} />
        <Route path="/diary-vault" element={<UserRoute user={user} role={role}><DiaryVault /></UserRoute>} />
        <Route path="/pricing"     element={<UserRoute user={user} role={role}><Pricing /></UserRoute>} />
        <Route path="/profile"     element={<UserRoute user={user} role={role}><MyProfile /></UserRoute>} />
        <Route path="/chattr"      element={<UserRoute user={user} role={role}><Chattr /></UserRoute>} />
        <Route path="/roadmap"     element={<UserRoute user={user} role={role}><Roadmap /></UserRoute>} />
        <Route path="/journal"     element={<UserRoute user={user} role={role}><JournalPage /></UserRoute>} />

        {/* ── Games Routes ── */}
        <Route path="/cluecue"     element={<UserRoute user={user} role={role}><ClueCuePage /></UserRoute>} />
        <Route path="/ttt"         element={<UserRoute user={user} role={role}><TicTacToe /></UserRoute>} />
        <Route path="/memory"      element={<UserRoute user={user} role={role}><MemoryMatch /></UserRoute>} />
        <Route path="/guess"       element={<UserRoute user={user} role={role}><NumberGuess /></UserRoute>} />
        <Route path="/scramble"    element={<UserRoute user={user} role={role}><WordScramble /></UserRoute>} />
        <Route path="/snake"       element={<UserRoute user={user} role={role}><SnakeGame /></UserRoute>} />
        <Route path="/reaction"    element={<UserRoute user={user} role={role}><ReactionTest /></UserRoute>} />
        <Route path="/math"        element={<UserRoute user={user} role={role}><MathSprint /></UserRoute>} />
        <Route path="/duel"        element={<UserRoute user={user} role={role}><TwoPlayerDuel /></UserRoute>} />

        {/* ── Community Feed (UNCHANGED — FeedProvider preserved) ── */}
        <Route path="/feed" element={
          user
            ? <UserRoute user={user} role={role}>
                <FeedProvider currentUser={user}><CommunityFeed /></FeedProvider>
              </UserRoute>
            : <Navigate to="/" />
        }/>

        {/* ── /mentors — both users AND mentors can access ── */}
        <Route path="/mentors"             element={user ? <MentorsPage /> : <Navigate to="/" />} />
        <Route path="/mentor-verification" element={user ? <MentorVerification /> : <Navigate to="/" />} />

        {/* ── Mentor-Only Routes ─────────────────────────── */}
        <Route path="/mentor-profile-setup" element={<MentorRoute user={user} role={role}><MentorProfileSetup /></MentorRoute>} />
        <Route path="/mentordashboard"      element={<MentorRoute user={user} role={role}><MentorDashboard /></MentorRoute>} />

        {/* ── Admin-Only Route ───────────────────────────── */}
        <Route path="/admin" element={<AdminRoute user={user} role={role}><AdminPanel /></AdminRoute>} />

        {/* ── Catch-all Fallback (UNCHANGED) ────────────── */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;

// ── Register PWA service worker (production only) ──
registerServiceWorker();